import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Loader2, Send, Shield, ShieldAlert, ArrowLeft, Flag, Ban, Check,
    X, Mail, Phone, Eye, EyeOff, UserX,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const SCAM_SAFETY_DISMISS_KEY = (convId) => `simonara_msg_safety_dismissed_${convId}`;

const REPORT_REASONS = [
    { v: "harassment", label: "Harassment or threats" },
    { v: "scam_fraud", label: "Scam or fraud" },
    { v: "discrimination", label: "Discrimination" },
    { v: "spam", label: "Spam / bulk messaging" },
    { v: "other", label: "Other" },
];

/**
 * Thread view. Both talent and employer render this; UI adapts via the
 * ``viewerRole`` prop ("professional" | "employer").
 */
export const MessageThread = ({
    conversation,
    viewerUserId,
    viewerRole,
    onBack,
    onConversationChange,
}) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [reportReason, setReportReason] = useState("other");
    const [reportDetails, setReportDetails] = useState("");
    const [reporting, setReporting] = useState(false);
    const [sharingContact, setSharingContact] = useState(false);
    const [safetyDismissed, setSafetyDismissed] = useState(false);
    const bottomRef = useRef(null);

    const convId = conversation.id;

    useEffect(() => {
        setSafetyDismissed(
            typeof window !== "undefined" &&
                !!localStorage.getItem(SCAM_SAFETY_DISMISS_KEY(convId)));
    }, [convId]);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/messaging/conversations/${convId}/messages`);
            setMessages(data.messages || []);
            if (data.conversation) {
                onConversationChange?.(data.conversation);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [convId, onConversationChange]);

    useEffect(() => { refresh(); }, [refresh]);
    // Auto-scroll to newest message.
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const closedOrBlocked = ["closed", "blocked"].includes(conversation.status);
    const isTalent = viewerRole === "professional";
    const otherLabel = isTalent ? "Employer" : (conversation.talent_accepted_at ? "Talent" : "Candidate (not yet accepted)");
    const needsTalentAccept = isTalent && !conversation.talent_accepted_at && !closedOrBlocked;

    const mutualRevealed = useMemo(() => {
        const list = conversation.contact_revealed_by || [];
        return list.includes(conversation.talent_user_id) && list.includes(conversation.employer_user_id);
    }, [conversation]);
    const viewerOptedIn = (conversation.contact_revealed_by || []).includes(viewerUserId);

    const send = async () => {
        const trimmed = body.trim();
        if (!trimmed) return;
        setSending(true);
        try {
            const { data } = await api.post(`/messaging/conversations/${convId}/messages`, { body: trimmed });
            setBody("");
            if (data.flagged_for_review) {
                toast("This message is flagged for admin review (safety keywords detected).", {
                    icon: <ShieldAlert size={14} className="text-amber-500" />,
                });
            }
            await refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSending(false);
        }
    };

    const accept = async () => {
        try {
            await api.post(`/messaging/conversations/${convId}/accept`);
            toast.success("Thread accepted. The employer can now see your full name.");
            onConversationChange?.({ ...conversation, talent_accepted_at: new Date().toISOString() });
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const decline = async () => {
        if (!window.confirm("Decline this conversation? The employer will see 'Not interested at this time'.")) return;
        try {
            await api.post(`/messaging/conversations/${convId}/decline`);
            toast.success("Conversation declined.");
            onConversationChange?.({ ...conversation, status: "closed" });
            onBack?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const block = async () => {
        if (!window.confirm("Block this person? They will no longer be able to message you and this thread will close.")) return;
        try {
            await api.post(`/messaging/conversations/${convId}/block`);
            toast.success("Blocked.");
            onConversationChange?.({ ...conversation, status: "blocked" });
            onBack?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const leave = async () => {
        if (!window.confirm("Leave this conversation? It will close for both sides.")) return;
        try {
            await api.post(`/messaging/conversations/${convId}/leave`);
            toast.success("Conversation closed.");
            onConversationChange?.({ ...conversation, status: "closed" });
            onBack?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const shareContact = async () => {
        setSharingContact(true);
        try {
            const { data } = await api.post(`/messaging/conversations/${convId}/share-contact`);
            if (data.mutual_revealed) toast.success("Contact details shared — mutual opt-in complete.");
            else toast.success("You've opted in. Waiting for the other side to opt in too.");
            await refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSharingContact(false); }
    };
    const submitReport = async () => {
        setReporting(true);
        try {
            await api.post(`/messaging/conversations/${convId}/report`, {
                reason: reportReason, details: reportDetails,
            });
            toast.success("Report submitted. Our trust team will review this thread.");
            setShowReport(false);
            setReportDetails("");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReporting(false); }
    };
    const dismissSafety = () => {
        try { localStorage.setItem(SCAM_SAFETY_DISMISS_KEY(convId), "1"); } catch (_e) { /* storage disabled */ }
        setSafetyDismissed(true);
    };

    return (
        <div className="flex flex-col h-[calc(100vh-220px)] min-h-[480px]" data-testid="msg-thread">
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-border pb-3 mb-3">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="rounded-full p-1.5 border border-border hover:bg-muted/40"
                        aria-label="Back"
                        data-testid="msg-thread-back"
                    >
                        <ArrowLeft size={14} />
                    </button>
                )}
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-heading font-semibold text-foreground truncate">
                        {otherLabel}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        Status: <span className="font-medium">{conversation.status}</span>
                        {conversation.job_id && <> · Job #{conversation.job_id.slice(-6)}</>}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {!closedOrBlocked && (
                        <>
                            <button
                                onClick={() => setShowReport(true)}
                                data-testid="msg-thread-report"
                                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-rose-700 dark:text-rose-400 hover:bg-rose-500/10"
                            >
                                <Flag size={11} /> Report
                            </button>
                            <button
                                onClick={isTalent ? block : leave}
                                data-testid={isTalent ? "msg-thread-block" : "msg-thread-leave"}
                                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted/50"
                            >
                                {isTalent
                                    ? <><Ban size={11} /> Block</>
                                    : <><UserX size={11} /> Leave</>}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Safety banner — talent side only, dismissible, only on fresh (not yet accepted) threads. */}
            {isTalent && !safetyDismissed && !conversation.talent_accepted_at && !closedOrBlocked && (
                <div data-testid="msg-safety-banner" className="mb-3 rounded-xl border border-amber-400/40 bg-amber-50/60 dark:bg-amber-500/10 px-4 py-3 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
                    <Shield size={14} className="shrink-0 mt-0.5 text-amber-600" />
                    <div className="flex-1">
                        <div className="font-semibold">Safety check before you reply</div>
                        <p className="mt-0.5 leading-relaxed opacity-90">
                            Simonara never asks you to pay fees, share bank details, or buy gift cards.
                            If anything feels off, use <strong>Report</strong> or <strong>Block</strong> — no
                            questions asked. Share phone/email only after you&apos;re ready.
                        </p>
                    </div>
                    <button
                        onClick={dismissSafety}
                        aria-label="Dismiss safety banner"
                        className="ml-2 shrink-0 rounded-full p-1 hover:bg-amber-500/15"
                        data-testid="msg-safety-dismiss"
                    >
                        <X size={12} />
                    </button>
                </div>
            )}

            {/* Accept / Decline bar — talent-only, only before first reply */}
            {needsTalentAccept && (
                <div data-testid="msg-accept-bar" className="mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-xs flex flex-wrap items-center gap-2">
                    <span className="flex-1 min-w-[180px] text-foreground">
                        Reply to accept this intro — your full name will be visible to the employer.
                    </span>
                    <button
                        onClick={accept}
                        data-testid="msg-accept"
                        className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1"
                    >
                        <Check size={11} /> Accept
                    </button>
                    <button
                        onClick={decline}
                        data-testid="msg-decline"
                        className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                    >
                        <X size={11} /> Not interested
                    </button>
                </div>
            )}

            {/* Contact share status */}
            <div className="mb-3 rounded-xl border border-border bg-card px-3 py-2 text-[11px] flex items-center gap-2 flex-wrap">
                {mutualRevealed ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                        <Eye size={12} /> Contact details shared on both sides
                    </span>
                ) : viewerOptedIn ? (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <EyeOff size={12} /> You&apos;ve opted in. Awaiting the other side.
                    </span>
                ) : (
                    <>
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <EyeOff size={12} /> {isTalent ? "Phone / email" : "Candidate phone / email"} stays private until mutual opt-in.
                        </span>
                        {!closedOrBlocked && (
                            <button
                                onClick={shareContact}
                                disabled={sharingContact}
                                data-testid="msg-share-contact"
                                className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 disabled:opacity-60"
                            >
                                {sharingContact
                                    ? <Loader2 size={11} className="animate-spin" />
                                    : (<><Mail size={10} /><Phone size={10} /> Share my contact</>)}
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Message list */}
            <div data-testid="msg-list" className="flex-1 overflow-y-auto space-y-2 pr-1">
                {loading ? (
                    <div className="py-10 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>
                ) : messages.length === 0 ? (
                    <div className="py-10 text-center text-xs text-muted-foreground">No messages yet.</div>
                ) : messages.map((m) => (
                    <MessageBubble key={m.id} m={m} isMine={m.sender_user_id === viewerUserId} />
                ))}
                <div ref={bottomRef} />
            </div>

            {/* Composer */}
            {closedOrBlocked ? (
                <div className="mt-3 rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground text-center" data-testid="msg-thread-closed">
                    This conversation is {conversation.status}. You can&apos;t send more messages.
                </div>
            ) : (
                <div className="mt-3 border-t border-border pt-3 flex items-end gap-2">
                    <Textarea
                        rows={2}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={isTalent ? "Write a reply…" : "Follow up…"}
                        data-testid="msg-composer-body"
                        className="flex-1 min-h-[44px] max-h-32"
                        maxLength={4000}
                    />
                    <button
                        onClick={send}
                        disabled={sending || !body.trim()}
                        data-testid="msg-composer-send"
                        className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60 self-end"
                    >
                        {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        Send
                    </button>
                </div>
            )}

            {/* Report modal */}
            <Dialog open={showReport} onOpenChange={setShowReport}>
                <DialogContent data-testid="msg-report-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Flag size={16} className="text-rose-500" /> Report this conversation
                        </DialogTitle>
                        <DialogDescription>
                            Our trust team reviews every report within 24 hours. Repeated reports
                            against the same account trigger an auto-pause.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Reason</label>
                            <Select value={reportReason} onValueChange={setReportReason}>
                                <SelectTrigger data-testid="msg-report-reason" className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {REPORT_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What happened? (optional)</label>
                            <Textarea
                                rows={4}
                                value={reportDetails}
                                onChange={(e) => setReportDetails(e.target.value)}
                                placeholder="Give us context so we can act quickly."
                                data-testid="msg-report-details"
                                maxLength={2000}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setShowReport(false)} className="btn-outline text-sm px-4 py-2" data-testid="msg-report-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={submitReport}
                            disabled={reporting}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="msg-report-submit"
                        >
                            {reporting ? <Loader2 size={13} className="animate-spin" /> : <Flag size={13} />}
                            Submit report
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const MessageBubble = ({ m, isMine }) => {
    const isSystem = m.sender_user_id === "system";
    if (isSystem) {
        return (
            <div data-testid={`msg-bubble-system-${m.id}`} className="my-2 flex justify-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/50 border border-border rounded-full px-3 py-1">
                    {m.body}
                </div>
            </div>
        );
    }
    return (
        <div data-testid={`msg-bubble-${m.id}`} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[76%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                isMine
                    ? "bg-emerald-600 text-white"
                    : "bg-card border border-border text-foreground"
            }`}>
                {m.body}
                <div className={`mt-1 text-[10px] ${isMine ? "text-emerald-50/80" : "text-muted-foreground"}`}>
                    {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
            </div>
        </div>
    );
};

export default MessageThread;
